import React, { useState, useRef } from 'react';
import { Rnd } from 'react-rnd';
import './ChecklistForm.css';

// Componente para edição inline da medida
type MedidaEditavelProps = {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
};

const MedidaEditavel: React.FC<MedidaEditavelProps> = ({ value, onChange, placeholder, style }) => {
  const [editando, setEditando] = useState(false);
  const [temp, setTemp] = useState(value);
  React.useEffect(() => { setTemp(value); }, [value]);
  return editando ? (
    <input
      type="text"
      value={temp}
      autoFocus
      onChange={e => setTemp(e.target.value)}
      onBlur={() => { setEditando(false); onChange(temp); }}
      onKeyDown={e => { if (e.key === 'Enter') { setEditando(false); onChange(temp); } }}
      style={{ ...style, minWidth: 80, textAlign: 'center' }}
      placeholder={placeholder}
    />
  ) : (
    <span onClick={() => setEditando(true)} style={style} tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') setEditando(true); }}>
      {value || <span style={{ color: '#bbb' }}>{placeholder}</span>}
    </span>
  );
};



const ChecklistForm: React.FC<{ checklistId?: number | null; onClose?: () => void }> = ({ checklistId, onClose }) => {
  // Tipagem para as caixas interativas com label descritivo
  type CaixaInterativa = {
    x: number;
    y: number;
    width: number;
    height: number;
    value: string;
    label: string; // Ex: "Largura total", "Altura lateral", etc
  };
  const [caixasDireito, setCaixasDireito] = useState<CaixaInterativa[]>([]);
  const [caixasEsquerdo, setCaixasEsquerdo] = useState<CaixaInterativa[]>([]);
  const [labelAtual, setLabelAtual] = useState<string>('Medida');
  const [loading, setLoading] = useState(false);

  // Adiciona caixa ao clicar na imagem da esquerda
  const handleAddCaixaEsquerdo = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Salvar também a largura real do container para escalar corretamente no PDF
    const containerWidth = rect.width;
    const containerHeight = rect.height;
    setCaixasEsquerdo(caixas => [...caixas, { x, y, width: 120, height: 30, value: '', label: labelAtual, containerWidth, containerHeight }]);
  };

  const [form, setForm] = useState({
    placa: '',
    cor_sider: '',
    azul_troca: '',
    cinza_remendo: '',
    banco_rasgado: '',
    todas_travessas: '',
    faltam_travessas: '',
    todas_travessas_correntes: '',
    todas_colunas_correntes: '',
    colunas_sem_corrente: '',
    sistema_pneumatico_vazamento: '',
    sistema_pneumatico_levanta4: '',
    banheira_pintada: '',
    freio_ok: '',
    todas_fivelas_instaladas: '',
    faltam_fivelas: '',
    trilho_aluminio: '',
    mecanismo_enrolar_lona: '',
    pneus_condicao: '',
    pneus_observacao: '',
    observacoes: '',
    medida_top: '',
    medida_left: '',
    medida_inner: '',
    medida_center: '',
    medida_right: '',
    medida_top_esq: '',
    medida_left_esq: '',
    medida_inner_esq: '',
    medida_center_esq: '',
    medida_right_esq: '',
  });

  // Adiciona caixa ao clicar na imagem
  const handleAddCaixaDireito = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Salvar também a largura real do container para escalar corretamente no PDF
    const containerWidth = rect.width;
    const containerHeight = rect.height;
    setCaixasDireito(caixas => [...caixas, { x, y, width: 120, height: 30, value: '', label: labelAtual, containerWidth, containerHeight }]);
  };

  const [placaSugestoes, setPlacaSugestoes] = useState<any[]>([]);
  const [showSugestoes, setShowSugestoes] = useState(false);
  const sugestoesRef = useRef<HTMLDivElement>(null);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'placa') {
      if (value.length >= 2) {
        fetch(`/api/checklist/autocomplete-placas?placa=${encodeURIComponent(value)}`)
          .then(res => res.json())
          .then(data => {
            setPlacaSugestoes(data || []);
            setShowSugestoes(true);
          });
      } else {
        setPlacaSugestoes([]);
        setShowSugestoes(false);
      }
    }
  };

  const handleSugestaoClick = (s: any) => {
    setForm({ ...form, placa: s.PLACA });
    setShowSugestoes(false);
  };

  // Fecha sugestões ao clicar fora
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sugestoesRef.current && !sugestoesRef.current.contains(event.target as Node)) {
        setShowSugestoes(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Carregar dados quando for edição
  React.useEffect(() => {
    if (checklistId) {
      setLoading(true);
      fetch(`/api/checklist/${checklistId}`)
        .then(res => res.json())
        .then(data => {
          setForm({
            placa: data.placa || '',
            cor_sider: data.cor_sider || '',
            azul_troca: data.azul_troca ? 'sim' : 'nao',
            cinza_remendo: data.cinza_remendo ? 'sim' : 'nao',
            banco_rasgado: data.banco_rasgado ? 'sim' : 'nao',
            todas_travessas: data.todas_travessas ? 'sim' : 'nao',
            faltam_travessas: data.faltam_travessas || '',
            todas_travessas_correntes: data.todas_travessas_correntes ? 'sim' : 'nao',
            todas_colunas_correntes: data.todas_colunas_correntes ? 'sim' : 'nao',
            colunas_sem_corrente: data.colunas_sem_corrente || '',
            sistema_pneumatico_vazamento: data.sistema_pneumatico_vazamento ? 'sim' : 'nao',
            sistema_pneumatico_levanta4: data.sistema_pneumatico_levanta4 ? 'sim' : 'nao',
            banheira_pintada: data.banheira_pintada ? 'sim' : 'nao',
            freio_ok: data.freio_ok ? 'sim' : 'nao',
            todas_fivelas_instaladas: data.todas_fivelas_instaladas ? 'sim' : 'nao',
            faltam_fivelas: data.faltam_fivelas || '',
            trilho_aluminio: data.trilho_aluminio || '',
            mecanismo_enrolar_lona: data.mecanismo_enrolar_lona || '',
            pneus_condicao: data.pneus_condicao || '',
            pneus_observacao: data.pneus_observacao || '',
            observacoes: data.observacoes || '',
            medida_top: '', medida_left: '', medida_inner: '', medida_center: '', medida_right: '',
            medida_top_esq: '', medida_left_esq: '', medida_inner_esq: '', medida_center_esq: '', medida_right_esq: ''
          });
          // Carregar medidas
          if (data.medidas_lado_esquerdo) {
            try {
              setCaixasEsquerdo(JSON.parse(data.medidas_lado_esquerdo));
            } catch (e) {}
          }
          if (data.medidas_lado_direito) {
            try {
              setCaixasDireito(JSON.parse(data.medidas_lado_direito));
            } catch (e) {}
          }
          setLoading(false);
        })
        .catch(() => {
          alert('Erro ao carregar dados do checklist');
          setLoading(false);
        });
    }
  }, [checklistId]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Montar payload convertendo sim/nao para boolean/int
    const payload = {
      ...form,
      azul_troca: form.azul_troca === 'sim' ? 1 : 0,
      cinza_remendo: form.cinza_remendo === 'sim' ? 1 : 0,
      banco_rasgado: form.banco_rasgado === 'sim' ? 1 : 0,
      todas_travessas: form.todas_travessas === 'sim' ? 1 : 0,
      faltam_travessas: form.faltam_travessas ? Number(form.faltam_travessas) : 0,
      todas_travessas_correntes: form.todas_travessas_correntes === 'sim' ? 1 : 0,
      todas_colunas_correntes: form.todas_colunas_correntes === 'sim' ? 1 : 0,
      sistema_pneumatico_vazamento: form.sistema_pneumatico_vazamento === 'sim' ? 1 : 0,
      sistema_pneumatico_levanta4: form.sistema_pneumatico_levanta4 === 'sim' ? 1 : 0,
      banheira_pintada: form.banheira_pintada === 'sim' ? 1 : 0,
      freio_ok: form.freio_ok === 'sim' ? 1 : 0,
      todas_fivelas_instaladas: form.todas_fivelas_instaladas === 'sim' ? 1 : 0,
      faltam_fivelas: form.faltam_fivelas ? Number(form.faltam_fivelas) : 0,
      medidas_lado_esquerdo: JSON.stringify(caixasEsquerdo),
      medidas_lado_direito: JSON.stringify(caixasDireito),
    };
    try {
      const url = checklistId ? `/api/checklist/${checklistId}` : '/api/checklist';
      const method = checklistId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        alert(checklistId ? 'Checklist atualizado com sucesso!' : 'Checklist enviado com sucesso!');
        if (!checklistId) {
          setForm({
            placa: '',
            cor_sider: '',
            azul_troca: '',
            cinza_remendo: '',
            banco_rasgado: '',
            todas_travessas: '',
            faltam_travessas: '',
            todas_travessas_correntes: '',
            todas_colunas_correntes: '',
            colunas_sem_corrente: '',
            sistema_pneumatico_vazamento: '',
            sistema_pneumatico_levanta4: '',
            banheira_pintada: '',
            freio_ok: '',
            todas_fivelas_instaladas: '',
            faltam_fivelas: '',
            trilho_aluminio: '',
            medida_top: '',
            medida_left: '',
            medida_inner: '',
            medida_center: '',
            medida_right: '',
            medida_top_esq: '',
            medida_left_esq: '',
            medida_inner_esq: '',
            medida_center_esq: '',
            medida_right_esq: '',
            mecanismo_enrolar_lona: '',
            pneus_condicao: '',
            pneus_observacao: '',
            observacoes: ''
          });
          setCaixasDireito([]);
          setCaixasEsquerdo([]);
        }
        // Atualizar dashboard/lista se necessário
        window.dispatchEvent(new Event('checklist-updated'));
        if (onClose) onClose();
      } else {
        alert('Erro ao enviar checklist!');
      }
    } catch (err) {
      alert('Erro ao conectar ao backend!');
    }
  };

  if (loading) {
    return <div style={{ padding: 20, textAlign: 'center' }}>Carregando...</div>;
  }

  return (
    <div style={{ width: '100%' }}>
      <form className="checklist-form" onSubmit={handleSubmit}>
        <h3>{checklistId ? 'Editar Checklist' : 'Checklist Carretas'}</h3>
        <label style={{ position: 'relative' }}>
          Placa da carreta:
          <input
            name="placa"
            value={form.placa}
            onChange={handleChange}
            autoComplete="off"
            required
            onFocus={() => { if (placaSugestoes.length > 0) setShowSugestoes(true); }}
          />
          {showSugestoes && placaSugestoes.length > 0 && (
            <div ref={sugestoesRef} style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#fff',
              border: '1px solid #ddd',
              zIndex: 10,
              maxHeight: 200,
              overflowY: 'auto',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              {placaSugestoes.map((s, i) => (
                <div
                  key={s.PLACA + i}
                  style={{ padding: 8, cursor: 'pointer', borderBottom: '1px solid #eee' }}
                  onClick={() => handleSugestaoClick(s)}
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter') handleSugestaoClick(s); }}
                >
                  <strong>{s.PLACA}</strong> - {s.FABRICANTE} {s.MODELO} {s.TIPO} {s.ANO_FABRICACAO} {s.STATUS && `(${s.STATUS})`}
                </div>
              ))}
            </div>
          )}
        </label>
        <label>
          Cor do Sider:
          <select name="cor_sider" value={form.cor_sider} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="azul">Azul</option>
            <option value="cinza">Cinza</option>
          </select>
        </label>
        {form.cor_sider === 'azul' && (
          <label>
            Azul é troca?
            <select name="azul_troca" value={form.azul_troca} onChange={handleChange} required>
              <option value="">Selecione</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </label>
        )}
        {form.cor_sider === 'cinza' && (
          <label>
            Cinza: precisa fazer remendo?
            <select name="cinza_remendo" value={form.cinza_remendo} onChange={handleChange} required>
              <option value="">Selecione</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </label>
        )}
        <label>
          Banco está rasgado?
          <select name="banco_rasgado" value={form.banco_rasgado} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>

        <label>
          Trilho de alumínio está reto ou ondulado?
          <select name="trilho_aluminio" value={form.trilho_aluminio} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="reto">Reto</option>
            <option value="ondulado">Ondulado</option>
          </select>
        </label>


        {/* Imagem do Sider com inputs posicionados nos pontos vermelhos */}
        {/* Seção de Medidas da Carreta */}
        <div style={{ margin: '32px 0', padding: '20px', background: '#f8f9fa', borderRadius: 12, border: '2px solid #3498db' }}>
          <h4 style={{ color: '#2c3e50', marginBottom: 16, fontSize: 18 }}>📏 Medidas da Carreta</h4>
          <div style={{ background: '#fff', padding: 16, borderRadius: 8, marginBottom: 20, border: '1px solid #e1e8ed' }}>
            <p style={{ color: '#555', marginBottom: 12, lineHeight: 1.6 }}>
              <strong>Instruções:</strong> Clique nas imagens abaixo nos pontos onde deseja adicionar uma medida. 
              Cada medida pode ser movida, redimensionada e rotulada. Use os botões abaixo para selecionar o tipo de medida antes de clicar.
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#2c3e50' }}>
                Selecione o tipo de medida a adicionar:
              </label>
              <select 
                value={labelAtual} 
                onChange={(e) => setLabelAtual(e.target.value)}
                style={{ 
                  padding: '8px 12px', 
                  borderRadius: 6, 
                  border: '1.5px solid #3498db',
                  fontSize: 14,
                  minWidth: 250,
                  cursor: 'pointer',
                  background: '#fff'
                }}
              >
                <option value="Largura total">Largura total</option>
                <option value="Altura lateral">Altura lateral</option>
                <option value="Comprimento">Comprimento</option>
                <option value="Altura interna">Altura interna</option>
                <option value="Largura interna">Largura interna</option>
                <option value="Espessura lateral">Espessura lateral</option>
                <option value="Distância entre eixos">Distância entre eixos</option>
                <option value="Altura do chassi">Altura do chassi</option>
                <option value="Medida">Medida (personalizada)</option>
              </select>
            </div>
            <div style={{ fontSize: 13, color: '#666', background: '#fffbea', padding: 10, borderRadius: 6, border: '1px solid #f4d03f' }}>
              💡 <strong>Dica:</strong> Você pode arrastar e redimensionar as caixas de medida após adicioná-las. Clique no X para remover.
            </div>
          </div>

        {/* Imagem do Sider - Lado Esquerdo (adicionada) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, margin: '24px 0' }}>
          <div style={{ textAlign: 'center' }}>
            <h5 style={{ color: '#e67e22', marginBottom: 12, fontSize: 16 }}>🔍 Lado Esquerdo da Carreta</h5>
            <div style={{ position: 'relative', display: 'inline-block', width: 900, maxWidth: '100%' }}>
              <div
                style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', zIndex: 2, cursor: 'crosshair' }}
                onClick={handleAddCaixaEsquerdo}
              >
                {caixasEsquerdo.map((caixa, idx) => (
                  <Rnd
                    key={idx}
                    size={{ width: caixa.width, height: caixa.height }}
                    position={{ x: caixa.x, y: caixa.y }}
                    onDragStop={(e, d) => {
                      setCaixasEsquerdo(caixas => caixas.map((c, i) => i === idx ? { ...c, x: d.x, y: d.y } : c));
                    }}
                    onResizeStop={(e, direction, ref, delta, position) => {
                      setCaixasEsquerdo(caixas => caixas.map((c, i) => i === idx ? {
                        ...c,
                        width: parseInt(ref.style.width, 10),
                        height: parseInt(ref.style.height, 10),
                        ...position
                      } : c));
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    bounds="parent"
                    style={{ zIndex: 3 }}
                  >
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      {/* Label da medida */}
                      <div style={{
                        position: 'absolute',
                        top: -22,
                        left: 0,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#e67e22',
                        background: 'rgba(255,255,255,0.95)',
                        padding: '2px 6px',
                        borderRadius: 4,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        border: '1px solid #f39c12'
                      }}>
                        {caixa.label}
                      </div>
                      <button
                        type="button"
                        aria-label="Excluir caixa"
                        onClick={e => {
                          e.stopPropagation();
                          setCaixasEsquerdo(caixas => caixas.filter((_, i) => i !== idx));
                        }}
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 18,
                          height: 18,
                          border: 'none',
                          background: '#e74c3c',
                          color: '#fff',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: 13,
                          zIndex: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.10)'
                        }}
                        tabIndex={0}
                      >
                        ×
                      </button>
                      <input
                        type="text"
                        value={caixa.value}
                        onChange={e => setCaixasEsquerdo(caixas => caixas.map((c, i) => i === idx ? { ...c, value: e.target.value } : c))}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        placeholder="Ex: 2.45m"
                        style={{
                          width: '100%',
                          height: '100%',
                          border: '1.5px solid #e67e22',
                          borderRadius: 8,
                          fontWeight: 600,
                          color: '#e67e22',
                          fontSize: 13,
                          textAlign: 'center',
                          background: 'rgba(255,255,255,0.96)',
                          boxShadow: '0 2px 8px rgba(230,126,34,0.15)',
                          padding: '2px 18px 2px 8px',
                          outline: 'none',
                          transition: 'border 0.2s, box-shadow 0.2s'
                        }}
                      />
                    </div>
                  </Rnd>
                ))}
              </div>
              <img src="/images/checklist/sider-esquerdo.png" alt="Sider Rebaixado - Lado Esquerdo" style={{ width: 900, maxWidth: '100%', border: '1px solid #eee', borderRadius: 8, position: 'relative', zIndex: 1 }} />
            </div>
            <div style={{ marginTop: 8, color: '#666', fontSize: 14 }}>
              <strong>Sider Rebaixado - Lado Esquerdo</strong>
              {caixasEsquerdo.length > 0 && <span style={{ color: '#27ae60', marginLeft: 8 }}>({caixasEsquerdo.length} medida{caixasEsquerdo.length > 1 ? 's' : ''} adicionada{caixasEsquerdo.length > 1 ? 's' : ''})</span>}
            </div>
          </div>
          {/* Imagem do Sider - Lado Direito (existente) */}
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <h5 style={{ color: '#e67e22', marginBottom: 12, fontSize: 16 }}>🔍 Lado Direito da Carreta</h5>
            <div style={{ position: 'relative', display: 'inline-block', width: 900, maxWidth: '100%' }}>
              <div
                style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', zIndex: 2, cursor: 'crosshair' }}
                onClick={handleAddCaixaDireito}
              >
                {caixasDireito.map((caixa, idx) => (
                  <Rnd
                    key={idx}
                    size={{ width: caixa.width, height: caixa.height }}
                    position={{ x: caixa.x, y: caixa.y }}
                    onDragStop={(e, d) => {
                      setCaixasDireito(caixas => caixas.map((c, i) => i === idx ? { ...c, x: d.x, y: d.y } : c));
                    }}
                    onResizeStop={(e, direction, ref, delta, position) => {
                      setCaixasDireito(caixas => caixas.map((c, i) => i === idx ? {
                        ...c,
                        width: parseInt(ref.style.width, 10),
                        height: parseInt(ref.style.height, 10),
                        ...position
                      } : c));
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    bounds="parent"
                    style={{ zIndex: 3 }}
                  >
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      {/* Label da medida */}
                      <div style={{
                        position: 'absolute',
                        top: -22,
                        left: 0,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#e67e22',
                        background: 'rgba(255,255,255,0.95)',
                        padding: '2px 6px',
                        borderRadius: 4,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        border: '1px solid #f39c12'
                      }}>
                        {caixa.label}
                      </div>
                      <button
                        type="button"
                        aria-label="Excluir caixa"
                        onClick={e => {
                          e.stopPropagation();
                          setCaixasDireito(caixas => caixas.filter((_, i) => i !== idx));
                        }}
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 18,
                          height: 18,
                          border: 'none',
                          background: '#e74c3c',
                          color: '#fff',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: 13,
                          zIndex: 10,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.10)'
                        }}
                        tabIndex={0}
                      >
                        ×
                      </button>
                      <input
                        type="text"
                        value={caixa.value}
                        onChange={e => setCaixasDireito(caixas => caixas.map((c, i) => i === idx ? { ...c, value: e.target.value } : c))}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        placeholder="Ex: 2.45m"
                        style={{
                          width: '100%',
                          height: '100%',
                          border: '1.5px solid #e67e22',
                          borderRadius: 8,
                          fontWeight: 600,
                          color: '#e67e22',
                          fontSize: 13,
                          textAlign: 'center',
                          background: 'rgba(255,255,255,0.96)',
                          boxShadow: '0 2px 8px rgba(230,126,34,0.15)',
                          padding: '2px 18px 2px 8px',
                          outline: 'none',
                          transition: 'border 0.2s, box-shadow 0.2s'
                        }}
                      />
                    </div>
                  </Rnd>
                ))}
              </div>
              <img src="/images/checklist/sider-direito.png" alt="Sider Rebaixado - Lado Direito" style={{ width: 900, maxWidth: '100%', border: '1px solid #eee', borderRadius: 8, position: 'relative', zIndex: 1 }} />
            </div>
            <div style={{ marginTop: 8, color: '#666', fontSize: 14 }}>
              <strong>Sider Rebaixado - Lado Direito</strong>
              {caixasDireito.length > 0 && <span style={{ color: '#27ae60', marginLeft: 8 }}>({caixasDireito.length} medida{caixasDireito.length > 1 ? 's' : ''} adicionada{caixasDireito.length > 1 ? 's' : ''})</span>}
            </div>
          </div>
        </div>
        </div>

        {/* ...existing code... */}
        <label>
          Mecanismo de enrolar lona:
          <input name="mecanismo_enrolar_lona" value={form.mecanismo_enrolar_lona} onChange={handleChange} placeholder="Descreva ou selecione" required />
        </label>
        <label>
          Está com todas as travessas?
          <select name="todas_travessas" value={form.todas_travessas} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        {form.todas_travessas === 'nao' && (
          <label>
            Faltam quantas travessas?
            <input name="faltam_travessas" type="number" min="0" value={form.faltam_travessas} onChange={handleChange} />
          </label>
        )}
        <label>
          Todas as travessas estão com correntes?
          <select name="todas_travessas_correntes" value={form.todas_travessas_correntes} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        <label>
          Todas as colunas estão com correntes?
          <select name="todas_colunas_correntes" value={form.todas_colunas_correntes} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        {form.todas_colunas_correntes === 'nao' && (
          <label>
            Quais colunas não estão com correntes?
            <input name="colunas_sem_corrente" value={form.colunas_sem_corrente} onChange={handleChange} />
          </label>
        )}
        <label>
          Sistema pneumático está com vazamento?
          <select name="sistema_pneumatico_vazamento" value={form.sistema_pneumatico_vazamento} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        <label>
          Está levantando os 4 lados?
          <select name="sistema_pneumatico_levanta4" value={form.sistema_pneumatico_levanta4} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        <label>
          A banheira da carreta está pintada?
          <select name="banheira_pintada" value={form.banheira_pintada} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        <label>
          Freio da carreta está OK?
          <select name="freio_ok" value={form.freio_ok} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        <label>
          Todas as fivelas estão instaladas?
          <select name="todas_fivelas_instaladas" value={form.todas_fivelas_instaladas} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        {form.todas_fivelas_instaladas === 'nao' && (
          <label>
            Quantas fivelas estão faltando?
            <input name="faltam_fivelas" type="number" min="0" value={form.faltam_fivelas} onChange={handleChange} />
          </label>
        )}
        <label>
          Condição dos pneus:
          <select name="pneus_condicao" value={form.pneus_condicao} onChange={handleChange} required>
            <option value="">Selecione</option>
            <option value="bons">Bons</option>
            <option value="marca_fogo">Com marca de fogo</option>
            <option value="com_avaria">Com avaria</option>
          </select>
        </label>
        {(form.pneus_condicao === 'marca_fogo' || form.pneus_condicao === 'com_avaria') && (
          <label>
            Detalhes sobre os pneus:
            <textarea 
              name="pneus_observacao" 
              value={form.pneus_observacao} 
              onChange={handleChange}
              placeholder="Descreva as marcas de fogo, avarias, localização dos pneus afetados, etc."
              rows={3}
            />
          </label>
        )}
        <label>
          Observações:
          <textarea name="observacoes" value={form.observacoes} onChange={handleChange} />
        </label>
        <button type="submit">Enviar Checklist</button>
      </form>
    </div>
  );
};

export default ChecklistForm;
